/**
 * The automatic window (decision record 2026-09-30, "Attribute insights and
 * Creative lab designs", replacing "shortest sufficient window"): 14 days,
 * widened to 28 when 14 days lack enough data. 7 days is a manual choice
 * only, because with the "6 creatives + $500" floors nearly every app clears
 * 7 days, which would make the noisiest window the default.
 *
 * GET /api/admaster/focus applies the same rule and its `window_days` is
 * trusted as is (it may also be a stored manual 7). This module is only for
 * the cases where the client has to choose: an explicit app selection in the
 * URL, or a Focus save without a window in view.
 */
import type { FocusApp } from './api';
import type { WindowDays } from './dates';

export const AUTOMATIC_WINDOW_DAYS = 14;
export const WIDENED_WINDOW_DAYS = 28;

const sufficientAt = (app: FocusApp, days: number): boolean => app.windows.some((w) => w.window_days === days && w.sufficient);

/**
 * The automatic window for a selection: 14 days when every selected app has
 * enough data over 14 days, else 28 (also when even 28 is not enough — the
 * app switcher says so). Apps not in the entitled list, and the portfolio
 * (no app named), get 14 days.
 */
export function recommendedWindow(appIds: readonly string[], apps: readonly FocusApp[] | undefined): WindowDays {
  const selected = (apps ?? []).filter((app) => appIds.includes(app.id));
  if (!selected.length) return AUTOMATIC_WINDOW_DAYS;
  return selected.every((app) => sufficientAt(app, AUTOMATIC_WINDOW_DAYS)) ? AUTOMATIC_WINDOW_DAYS : WIDENED_WINDOW_DAYS;
}

/** The evidence behind the automatic window, for one app (shown in the date picker). */
export function windowHint(appIds: readonly string[], portfolio: boolean, apps: readonly FocusApp[] | undefined): string | undefined {
  if (portfolio || appIds.length !== 1 || !apps) return undefined;
  const app = apps.find((a) => a.id === appIds[0]);
  if (!app) return undefined;
  if (sufficientAt(app, AUTOMATIC_WINDOW_DAYS)) return 'Last 14 days has enough data for this app.';
  if (sufficientAt(app, WIDENED_WINDOW_DAYS)) return 'Only Last 28 days has enough data for this app.';
  return 'No window up to 28 days has enough data for this app yet.';
}
